import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "step",
    "progressBar",
    "stepLabel",
    "stepName",
    "dot",
    "nextButton",
    "backButton",
    "submitButton",
    "usernameInput",
    "usernameStatus",
    "businessName",
    "form",
    "errors",
    "selectionCount",
    "userUsernameInput",
    "userUsernameStatus",
    "organizationType"
  ]

  static values = {
    currentStep: { type: Number, default: 1 }
  }

  // Track username validation state
  orgUsernameValid = false
  userUsernameValid = true // Default true for logged-in users who skip this step
  generatedBusinessName = ""
  generatedOrgUsername = ""

  connect() {
    this.totalSteps = this.stepTargets.length
    this.selectBusinessType()
    this.updateUI()

    // Add input listeners to account form fields (step 3 for non-logged-in users)
    this.element.querySelectorAll('input[name^="user["]').forEach(input => {
      input.addEventListener('input', () => this.updateNextButtonState())
    })
  }

  selectBusinessType(event) {
    // Update visual selection state for all business type cards (checkboxes)
    const cards = this.element.querySelectorAll('.business-type-card')
    let selectedCount = 0
    let firstSelectedType = null

    cards.forEach(card => {
      const checkbox = card.querySelector('input[type="checkbox"]')
      const cardDiv = card.querySelector('div')
      const iconContainer = cardDiv?.querySelector('.w-12')
      const icon = iconContainer?.querySelector('i')

      if (checkbox?.checked) {
        selectedCount++
        // Track the first selected type for organization_type
        if (!firstSelectedType) {
          firstSelectedType = checkbox.value
        }
        // Selected state
        cardDiv?.classList.remove('border-gray-200', 'dark:border-gray-700', 'bg-white', 'dark:bg-gray-800')
        cardDiv?.classList.add('border-emerald-500', 'bg-emerald-50', 'dark:bg-emerald-900/20', 'ring-2', 'ring-emerald-500', 'ring-opacity-50')
        iconContainer?.classList.remove('bg-gray-100', 'dark:bg-gray-700')
        iconContainer?.classList.add('bg-emerald-100', 'dark:bg-emerald-800')
        icon?.classList.remove('text-gray-500', 'dark:text-gray-400')
        icon?.classList.add('text-emerald-600', 'dark:text-emerald-400')
      } else {
        // Unselected state
        cardDiv?.classList.add('border-gray-200', 'dark:border-gray-700', 'bg-white', 'dark:bg-gray-800')
        cardDiv?.classList.remove('border-emerald-500', 'bg-emerald-50', 'dark:bg-emerald-900/20', 'ring-2', 'ring-emerald-500', 'ring-opacity-50')
        iconContainer?.classList.add('bg-gray-100', 'dark:bg-gray-700')
        iconContainer?.classList.remove('bg-emerald-100', 'dark:bg-emerald-800')
        icon?.classList.add('text-gray-500', 'dark:text-gray-400')
        icon?.classList.remove('text-emerald-600', 'dark:text-emerald-400')
      }
    })

    // Set the hidden organization_type field to the first selected service type
    if (this.hasOrganizationTypeTarget) {
      this.organizationTypeTarget.value = firstSelectedType || ''
    }

    // Update selection count display
    if (this.hasSelectionCountTarget) {
      if (selectedCount === 0) {
        this.selectionCountTarget.textContent = ''
      } else if (selectedCount === 1) {
        this.selectionCountTarget.textContent = '1 service selected'
      } else {
        this.selectionCountTarget.textContent = `${selectedCount} services selected`
      }
    }

    this.updateNextButtonState()
  }

  updateUsername() {
    const username = this.usernameInputTarget.value.trim().toLowerCase()

    // Suggest a business name from the URL, but do not overwrite a real name.
    if (username && this.hasBusinessNameTarget) {
      const businessName = username
        .split(/[-_]/)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ')
      if (!this.businessNameTarget.value.trim() || this.businessNameTarget.value === this.generatedBusinessName) {
        this.businessNameTarget.value = businessName
        this.generatedBusinessName = businessName
      }
    }

    // Check username availability with debounce
    this.checkOrgUsername(username)
  }

  updateBusinessName() {
    if (!this.hasBusinessNameTarget || !this.hasUsernameInputTarget) return

    const businessName = this.businessNameTarget.value.trim()
    const currentUsername = this.usernameInputTarget.value.trim()
    const suggestedUsername = this.slugFromBusinessName(businessName)

    if (!suggestedUsername) {
      if (!currentUsername || currentUsername === this.generatedOrgUsername) {
        this.usernameInputTarget.value = ""
        this.generatedOrgUsername = ""
        this.orgUsernameValid = false
        this.showUsernameStatus('usernameStatus', '', 'neutral')
        this.updateNextButtonState()
      }
      return
    }

    // Keep explicit owner edits. Only replace a blank slug or one we generated.
    if (!currentUsername || currentUsername === this.generatedOrgUsername) {
      this.usernameInputTarget.value = suggestedUsername
      this.generatedOrgUsername = suggestedUsername
      this.checkOrgUsername(suggestedUsername)
    } else {
      this.updateNextButtonState()
    }
  }

  slugFromBusinessName(name) {
    return name
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .replace(/_{2,}/g, '_')
      .slice(0, 40)
      .replace(/^_+|_+$/g, '')
  }

  // Debounced username check for organization
  checkOrgUsername(username) {
    // Clear previous timeout
    if (this.orgUsernameTimeout) {
      clearTimeout(this.orgUsernameTimeout)
    }

    // Reset state while typing
    this.orgUsernameValid = false
    this.updateNextButtonState()

    if (!username || username.length < 3) {
      this.showUsernameStatus('usernameStatus', '', 'neutral')
      return
    }

    // Show checking state
    this.showUsernameStatus('usernameStatus', 'Checking...', 'checking')

    // Debounce the API call
    this.orgUsernameTimeout = setTimeout(async () => {
      try {
        const response = await fetch(`/check_username?username=${encodeURIComponent(username)}&type=organization`)
        const data = await response.json()

        if (data.available) {
          this.orgUsernameValid = true
          this.showUsernameStatus('usernameStatus', data.message, 'success')
        } else {
          this.orgUsernameValid = false
          this.showUsernameStatus('usernameStatus', data.message, 'error')
        }
        this.updateNextButtonState()
      } catch (error) {
        console.error('Username check failed:', error)
        this.showUsernameStatus('usernameStatus', 'Could not verify', 'error')
      }
    }, 400)
  }

  // Check user username availability (for account creation step)
  checkUserUsername(event) {
    const username = event.target.value.trim().toLowerCase()

    // Clear previous timeout
    if (this.userUsernameTimeout) {
      clearTimeout(this.userUsernameTimeout)
    }

    // Reset state while typing
    this.userUsernameValid = false
    this.updateNextButtonState()

    if (!username || username.length < 3) {
      this.showUsernameStatus('userUsernameStatus', '', 'neutral')
      return
    }

    // Check if it matches the organization username (can't be the same!)
    const orgUsername = this.hasUsernameInputTarget ? this.usernameInputTarget.value.trim().toLowerCase() : ''
    if (username === orgUsername) {
      this.userUsernameValid = false
      this.showUsernameStatus('userUsernameStatus', "Can't be the same as your business username", 'error')
      this.updateNextButtonState()
      return
    }

    // Show checking state
    this.showUsernameStatus('userUsernameStatus', 'Checking...', 'checking')

    // Debounce the API call
    this.userUsernameTimeout = setTimeout(async () => {
      try {
        const response = await fetch(`/check_username?username=${encodeURIComponent(username)}&type=user`)
        const data = await response.json()

        if (data.available) {
          this.userUsernameValid = true
          this.showUsernameStatus('userUsernameStatus', data.message, 'success')
        } else {
          this.userUsernameValid = false
          this.showUsernameStatus('userUsernameStatus', data.message, 'error')
        }
        this.updateNextButtonState()
      } catch (error) {
        console.error('Username check failed:', error)
        this.showUsernameStatus('userUsernameStatus', 'Could not verify', 'error')
      }
    }, 400)
  }

  // Show username validation status
  showUsernameStatus(targetName, message, state) {
    const target = targetName === 'usernameStatus' ? this.usernameStatusTarget : this.userUsernameStatusTarget

    if (!target) return

    target.textContent = message

    // Remove all state classes
    target.classList.remove('text-gray-500', 'text-emerald-600', 'text-red-500', 'text-blue-500')

    // Add appropriate state class and icon
    switch (state) {
      case 'success':
        target.innerHTML = `<i class="fas fa-check-circle mr-1"></i>${message}`
        target.classList.add('text-emerald-600')
        break
      case 'error':
        target.innerHTML = `<i class="fas fa-times-circle mr-1"></i>${message}`
        target.classList.add('text-red-500')
        break
      case 'checking':
        target.innerHTML = `<i class="fas fa-spinner fa-spin mr-1"></i>${message}`
        target.classList.add('text-blue-500')
        break
      default:
        target.innerHTML = message
        target.classList.add('text-gray-500')
    }
  }

  nextStep() {
    if (!this.validateCurrentStep()) return

    if (this.currentStepValue < this.totalSteps) {
      this.currentStepValue++
      this.updateUI()
      this.scrollToTop()
    }
  }

  previousStep() {
    if (this.currentStepValue > 1) {
      this.currentStepValue--
      this.updateUI()
      this.scrollToTop()
    }
  }

  validateCurrentStep() {
    const currentStepEl = this.stepTargets.find(
      step => parseInt(step.dataset.step) === this.currentStepValue
    )

    if (!currentStepEl) return true

    // Step 1: At least one business type must be selected
    if (this.currentStepValue === 1) {
      if (this.element.dataset.existingProfileSelected === "true") {
        this.showValidationError("Claim the existing profile instead of creating a duplicate")
        return false
      }

      const selected = currentStepEl.querySelectorAll('input[type="checkbox"]:checked')
      if (selected.length === 0) {
        this.showValidationError("Please select at least one service type")
        return false
      }
    }

    // Step 2: Username must be filled AND available
    if (this.currentStepValue === 2) {
      if (this.hasBusinessNameTarget && !this.businessNameTarget.value.trim()) {
        this.showValidationError("Please enter your business name")
        return false
      }
      if (this.hasUsernameInputTarget && !this.usernameInputTarget.value.trim()) {
        this.showValidationError("Please enter a username for your business")
        return false
      }
      if (!this.orgUsernameValid) {
        this.showValidationError("Please choose an available username")
        return false
      }
    }

    // Step 3 (if exists for non-logged-in users): Account info
    if (this.currentStepValue === 3 && this.totalSteps === 4) {
      const email = currentStepEl.querySelector('input[name="user[email]"]')
      const username = currentStepEl.querySelector('input[name="user[username]"]')
      const password = currentStepEl.querySelector('input[name="user[password]"]')
      const passwordConfirm = currentStepEl.querySelector('input[name="user[password_confirmation]"]')

      if (!email?.value?.trim()) {
        this.showValidationError("Please enter your email")
        return false
      }
      if (!username?.value?.trim()) {
        this.showValidationError("Please choose a username")
        return false
      }
      if (!this.userUsernameValid) {
        this.showValidationError("Please choose an available username")
        return false
      }
      if (!password?.value || password.value.length < 6) {
        this.showValidationError("Password must be at least 6 characters")
        return false
      }
      if (password?.value !== passwordConfirm?.value) {
        this.showValidationError("Passwords don't match")
        return false
      }
    }

    this.clearValidationError()
    return true
  }

  showValidationError(message) {
    // Create or update error display
    let errorEl = this.element.querySelector('.wizard-validation-error')
    if (!errorEl) {
      errorEl = document.createElement('div')
      errorEl.className = 'wizard-validation-error bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-xl p-4 mb-6'
      errorEl.innerHTML = `
        <div class="flex items-center">
          <i class="fas fa-exclamation-circle text-red-500 mr-3"></i>
          <span class="text-sm text-red-700 dark:text-red-300"></span>
        </div>
      `
      this.formTarget.insertBefore(errorEl, this.formTarget.firstChild)
    }
    errorEl.querySelector('span').textContent = message
    errorEl.classList.remove('hidden')
    this.scrollToTop()
  }

  clearValidationError() {
    const errorEl = this.element.querySelector('.wizard-validation-error')
    if (errorEl) {
      errorEl.classList.add('hidden')
    }
  }

  updateUI() {
    // Update step visibility
    this.stepTargets.forEach(step => {
      const stepNum = parseInt(step.dataset.step)
      if (stepNum === this.currentStepValue) {
        step.classList.remove('hidden')
        step.classList.add('animate-fadeIn')
      } else {
        step.classList.add('hidden')
        step.classList.remove('animate-fadeIn')
      }
    })

    // Update progress bar (if exists)
    if (this.hasProgressBarTarget) {
      const progress = (this.currentStepValue / this.totalSteps) * 100
      this.progressBarTarget.style.width = `${progress}%`
    }

    // Update step label (if exists)
    if (this.hasStepLabelTarget) {
      this.stepLabelTarget.textContent = `Step ${this.currentStepValue} of ${this.totalSteps}`
    }

    // Update step name (if exists)
    if (this.hasStepNameTarget) {
      const stepNames = this.totalSteps === 3
        ? ['Business Type', 'Username', 'Your Account']
        : ['Business Type', 'Username']
      this.stepNameTarget.textContent = stepNames[this.currentStepValue - 1] || ''
    }

    // Update progress dots (if exist)
    if (this.hasDotTarget) {
      this.dotTargets.forEach((dot, i) => {
        dot.classList.remove('active', 'done')
        if (i + 1 < this.currentStepValue) dot.classList.add('done')
        else if (i + 1 === this.currentStepValue) dot.classList.add('active')
      })
    }

    // Update navigation buttons
    this.updateNavigationButtons()
  }

  updateNavigationButtons() {
    const isFirstStep = this.currentStepValue === 1
    const isLastStep = this.currentStepValue === this.totalSteps

    // Back button
    if (isFirstStep) {
      this.backButtonTarget.classList.add('hidden')
    } else {
      this.backButtonTarget.classList.remove('hidden')
    }

    // Next vs Submit button
    if (isLastStep) {
      this.nextButtonTarget.classList.add('hidden')
      this.submitButtonTarget.classList.remove('hidden')
    } else {
      this.nextButtonTarget.classList.remove('hidden')
      this.submitButtonTarget.classList.add('hidden')
    }

    // Update next button state
    this.updateNextButtonState()
  }

  updateNextButtonState() {
    const currentStepEl = this.stepTargets.find(
      step => parseInt(step.dataset.step) === this.currentStepValue
    )

    let isValid = false

    // Step 1: Check if at least one business type selected
    if (this.currentStepValue === 1) {
      const selectedCount = currentStepEl?.querySelectorAll('input[type="checkbox"]:checked').length || 0
      const existingProfileSelected = this.element.dataset.existingProfileSelected === "true"
      isValid = selectedCount > 0 && !existingProfileSelected
    }
    // Step 2: Check if username filled AND available
    else if (this.currentStepValue === 2) {
      const hasBusinessName = this.hasBusinessNameTarget && this.businessNameTarget.value.trim().length > 0
      const hasUsername = this.hasUsernameInputTarget && this.usernameInputTarget.value.trim().length > 0
      isValid = hasBusinessName && hasUsername && this.orgUsernameValid
    }
    // Step 3: Account step (only for non-logged-in)
    else if (this.currentStepValue === 3 && this.totalSteps === 4) {
      const email = currentStepEl?.querySelector('input[name="user[email]"]')
      const username = currentStepEl?.querySelector('input[name="user[username]"]')
      const password = currentStepEl?.querySelector('input[name="user[password]"]')
      isValid = email?.value?.trim() && username?.value?.trim() && password?.value?.length >= 6 && this.userUsernameValid
    }
    // Last step (plans): Always valid since there's a default selection
    else {
      isValid = true
    }

    this.nextButtonTarget.disabled = !isValid
  }

  scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
}
